import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Trash2, Plus, LogOut, ShieldCheck } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export default function Admin() {
    const [authed, setAuthed] = useState(false);
    const [password, setPassword] = useState("");
    const [loginErr, setLoginErr] = useState("");
    const [entries, setEntries] = useState([]);
    const [value, setValue] = useState("");
    const [label, setLabel] = useState("");
    const [addErr, setAddErr] = useState("");

    const loadWhitelist = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/whitelist`, cfg);
            setEntries(res.data.entries);
            setAuthed(true);
        } catch {
            setAuthed(false);
        }
    }, []);

    useEffect(() => {
        axios.get(`${API}/admin/session`, cfg).then((r) => {
            if (r.data.authenticated) loadWhitelist();
        });
    }, [loadWhitelist]);

    const login = async (e) => {
        e.preventDefault();
        setLoginErr("");
        try {
            await axios.post(`${API}/admin/login`, { password }, cfg);
            await loadWhitelist();
        } catch {
            setLoginErr("Incorrect password.");
        }
    };

    const logout = async () => {
        await axios.post(`${API}/admin/logout`, {}, cfg);
        setAuthed(false);
    };

    const addEntry = async (e) => {
        e.preventDefault();
        setAddErr("");
        try {
            await axios.post(`${API}/admin/whitelist`, { value, label }, cfg);
            setValue(""); setLabel("");
            loadWhitelist();
        } catch (err) {
            setAddErr(err.response?.data?.detail || "Could not add entry.");
        }
    };

    const remove = async (id) => {
        await axios.delete(`${API}/admin/whitelist/${id}`, cfg);
        loadWhitelist();
    };

    if (!authed) {
        return (
            <div className="min-h-screen flex flex-col bg-white">
                <Header />
                <main className="flex-1 flex items-center justify-center px-4">
                    <form onSubmit={login} className="w-full max-w-sm bg-white border border-line rounded-lg p-8" data-testid="admin-login">
                        <Lock className="w-8 h-8 text-aqua mx-auto" />
                        <h1 className="text-2xl font-bold text-asphalt text-center mt-4">Admin access</h1>
                        <p className="text-sm text-muted text-center mt-1">IP allowlist manager</p>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-password" placeholder="Password"
                            className="w-full mt-6 px-4 py-3 border border-line rounded-md outline-none focus:border-aqua" />
                        {loginErr && <p className="text-sm text-danger mt-2">{loginErr}</p>}
                        <button type="submit" data-testid="admin-login-btn"
                            className="w-full mt-4 px-4 py-3 rounded-md bg-aqua text-asphalt font-medium hover:brightness-95 transition-all">
                            Sign in
                        </button>
                    </form>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Header />
            <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-12">
                <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-bold text-asphalt flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-aqua" /> IP Allowlist</h1>
                    <button onClick={logout} className="text-sm font-medium text-asphalt hover:text-aqua flex items-center gap-1.5"><LogOut className="w-4 h-4" /> Log out</button>
                </div>
                <p className="text-sm text-muted mt-2">Manage which IPs or CIDR ranges may call the public /api/audit endpoint.</p>

                <form onSubmit={addEntry} className="mt-6 flex flex-col sm:flex-row gap-3">
                    <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="wl-value"
                        placeholder="IP or CIDR e.g. 203.0.113.4 or 10.0.0.0/24"
                        className="flex-1 px-4 py-3 border border-line rounded-md outline-none focus:border-aqua" />
                    <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="wl-label"
                        placeholder="Label"
                        className="sm:w-40 px-4 py-3 border border-line rounded-md outline-none focus:border-aqua" />
                    <button type="submit" data-testid="wl-add"
                        className="px-5 py-3 rounded-md bg-aqua text-asphalt font-medium inline-flex items-center gap-1.5 justify-center"><Plus className="w-4 h-4" /> Add</button>
                </form>
                {addErr && <p className="text-sm text-danger mt-2">{addErr}</p>}

                <div className="mt-6 border border-line rounded-lg overflow-hidden">
                    {entries.length === 0 && <p className="p-4 text-sm text-muted">No entries yet.</p>}
                    {entries.map((e) => (
                        <div key={e.id} className="flex items-center justify-between px-4 py-3 border-b border-line last:border-0" data-testid={`wl-row-${e.value}`}>
                            <div>
                                <p className="text-sm font-medium text-asphalt">{e.value}</p>
                                <p className="text-xs text-muted">{e.label || "no label"} · {new Date(e.created_at).toLocaleString("en-GB")}</p>
                            </div>
                            <button onClick={() => remove(e.id)} className="text-muted hover:text-danger" data-testid={`wl-delete-${e.value}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                    ))}
                </div>
            </main>
            <Footer />
        </div>
    );
}
